import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Pencil, Trash2, Star, Clock, History, ChevronDown, ChevronUp, Link2, Flame, Eye, EyeOff, LockKeyhole } from "lucide-react";
import { EventBadges, ConfidenceBadge } from "@/components/CatalystBadges";
import { catMeta, confMeta, fmtAgo, isTbd, isCancelled, isUnpublished, EVENT_TYPE_LABEL } from "@/lib/catalysts";

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Date TBD");
const LOG_LABEL = {
  created: "Added", edited: "Edited by admin", approved: "Approved", published: "Published to Pro", hidden: "Hidden from Pro", unlocked: "Following sources again", rescheduled: "Rescheduled", cancelled: "Cancelled", viral: "Marked viral",
  date_tbd: "Date became TBD", date_set: "Date announced", source_added: "New source", merged: "Merged duplicate", conflict: "Conflicting report", details: "Details updated",
};

export default function CatalystDetail({ event, onClose, isAdmin, isStarred, onStar, onEdit, onDelete, onTogglePublish, onUnlock }) {

const money = (v) => (v == null ? "—" : Math.abs(v) >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Number(v).toLocaleString()}`);
const eps = (v) => (v == null ? "—" : Number(v).toFixed(2));
const RESULT_CLS = { beat: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", missed: "text-red-400 border-red-500/40 bg-red-500/10", mixed: "text-amber-300 border-amber-500/40 bg-amber-500/10" };

function EarningsBlock({ er }) {
  const row = (label, est, act) => {
    const v = act == null || est == null ? null : act >= est ? "beat" : "missed";
    return (
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-baseline text-xs" key={label}>
        <span className="text-slate-400">{label}</span>
        <span className="font-mono text-slate-300">{est == null ? "—" : label === "EPS" ? eps(est) : money(est)} <span className="text-slate-600">est</span></span>
        <span className={`font-mono ${v === "beat" ? "text-emerald-400" : v === "missed" ? "text-red-400" : "text-slate-500"}`}>{act == null ? "pending" : `${label === "EPS" ? eps(act) : money(act)} actual`}</span>
      </div>
    );
  };
  return (
    <div className="rounded-lg border border-[#222F43] bg-[#0d1420] p-3 space-y-1.5" data-testid="detail-earnings">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{er.symbol} · Q{er.quarter} {er.year} · {er.hour === "bmo" ? "Before Open" : er.hour === "amc" ? "After Close" : er.hour === "dmh" ? "During Market Hours" : "Time TBA"} (ET)</span>
        {er.result && <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${RESULT_CLS[er.result]}`} data-testid="earnings-result">{er.result}</span>}
      </div>
      {row("EPS", er.epsEstimate, er.epsActual)}
      {row("Revenue", er.revenueEstimate, er.revenueActual)}
    </div>
  );
}

  const [showLog, setShowLog] = useState(false);
  const e = event;
  return (
    <Dialog open={Boolean(e)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-[#334566] max-w-md max-h-[90vh] overflow-y-auto" data-testid="catalyst-detail-dialog">
        {e && (() => {
          const cm = catMeta(e.category); const cf = confMeta(e.confidence);
          const sources = e.sources?.length ? e.sources : (e.sourceUrl ? [{ url: e.sourceUrl, name: e.sourceName || "source" }] : []);
          const log = [...(e.changelog || [])].reverse();
          return (
            <>
              <DialogHeader>
                <DialogTitle className={`font-display flex items-start gap-2 ${isCancelled(e) ? "line-through text-slate-400" : ""}`}>
                  <span className="h-3 w-3 rounded-full mt-1.5 shrink-0" style={{ background: cm.color }} />
                  {e.title}
                </DialogTitle>
                <DialogDescription className="text-slate-400 flex items-center gap-2 flex-wrap">
                  <span data-testid="detail-date">{isTbd(e) ? `Date TBD${e.dateHint ? ` · ${e.dateHint}` : ""}` : fmtDay(e.date)}</span>
                  {e.time && !isTbd(e) && <span className="flex items-center gap-1 text-slate-300" data-testid="detail-time"><Clock className="h-3 w-3" /> {e.time}</span>}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <EventBadges e={e} />
                  {isAdmin && isUnpublished(e) && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="detail-unpublished-badge">{e.review === "hidden" ? "hidden from Pro" : "not published yet"}</span>}
                  {e.source === "auto" && <span className="text-[10px] text-cyan-300/80">{e.confidence && e.confidence !== "confirmed" ? "official source · date unconfirmed" : "official source"}</span>}
                </div>
                {e.confidence && (
                  <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="detail-confidence">
                    <ConfidenceBadge e={e} />
                    <span>{e.confidence === "confirmed" ? "Official announcement or schedule" : e.confidence === "reported" ? "Credible reporting, not officially confirmed" : e.confidence === "estimated" ? "Projected date — not yet confirmed by the company" : "Leak / speculation — treat with caution"}</span>
                    <span className="sr-only">{cf.label}</span>
                  </div>
                )}
                {e.status === "rescheduled" && e.dateHistory?.length > 0 && (
                  <p className="text-xs text-amber-300/90" data-testid="detail-rescheduled-note">Previously {fmtDay(e.dateHistory[e.dateHistory.length - 1].date)}</p>
                )}
                {e.description && <p className="text-sm text-slate-300">{e.description}</p>}
                {e.earnings && <EarningsBlock er={e.earnings} />}
                {(e.eventType || (e.parties || []).length > 0 || (e.platforms || []).length > 0 || e.studio) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400" data-testid="detail-facts">
                    {e.eventType && EVENT_TYPE_LABEL[e.eventType] && <span>What: <span className="text-slate-200">{EVENT_TYPE_LABEL[e.eventType]}</span></span>}
                    {(e.parties || []).length > 0 && <span>Parties: <span className="text-slate-200">{e.parties.join(" · ")}</span></span>}
                    {(e.platforms || []).length > 0 && <span>Platforms: <span className="text-slate-200">{e.platforms.join(", ")}</span></span>}
                    {e.studio && <span>Studio: <span className="text-slate-200">{e.studio}</span></span>}
                  </div>
                )}
                {e.viral && (
                  <div className="rounded-lg border border-orange-500/40 bg-orange-500/5 p-2.5 text-xs" data-testid="detail-viral">
                    <div className="flex items-center gap-1.5 text-orange-300 font-semibold mb-1"><Flame className="h-3.5 w-3.5" /> Viral case — in everyone's My Feed</div>
                    <div className="text-slate-400">{(e.viralReasons || []).join(" · ")}</div>
                    {(e.relatedTokens || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {e.relatedTokens.map((t) => (
                          <a key={t.address} href={`/?chain=${t.chain}&address=${t.address}`} className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-[#172030] text-emerald-400 hover:underline" data-testid={`detail-token-${t.symbol}`}>
                            ${t.symbol} <span className="text-slate-500">· {t.chain} · ${Math.round(t.liquidityUsd / 1000)}k liq</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {(e.coins || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">{e.coins.map((c) => <span key={c} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-[#172030] text-emerald-400">${c}</span>)}</div>
                )}
                {sources.length > 0 && (
                  <div data-testid="detail-sources">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><Link2 className="h-3 w-3" /> Sources ({sources.length})</div>
                    <ul className="space-y-1">
                      {sources.map((s, i) => (
                        <li key={s.url || i} className="text-xs">
                          <a href={s.url} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline flex items-start gap-1" data-testid={`detail-source-${i}`}>
                            <ExternalLink className="h-3 w-3 mt-0.5 shrink-0" />
                            <span className="min-w-0"><span className="font-semibold">{s.name || "source"}</span>{s.title && <span className="text-slate-400"> — {s.title.slice(0, 80)}{s.title.length > 80 ? "…" : ""}</span>}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {isAdmin && !e.private && (e.adminLocked || []).length > 0 && (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 px-2.5 py-1.5 text-xs" data-testid="detail-locked">
                    <span className="text-amber-300/90 flex items-center gap-1.5 min-w-0"><LockKeyhole className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Admin-locked: {e.adminLocked.join(", ")}</span></span>
                    {onUnlock && <button onClick={() => onUnlock(e)} className="text-slate-400 hover:text-emerald-300 whitespace-nowrap" data-testid="detail-unlock-btn">Follow sources again</button>}
                  </div>
                )}
                <div className="flex items-center justify-between text-[11px] text-slate-500 font-mono">
                  <span data-testid="detail-updated">{e.updated_at || e.created_at ? `Updated ${fmtAgo(e.updated_at || e.created_at)}` : ""}</span>
                  {log.length > 0 && (
                    <button onClick={() => setShowLog((v) => !v)} className="flex items-center gap-1 hover:text-slate-300" data-testid="detail-log-toggle">
                      <History className="h-3 w-3" /> History ({log.length}) {showLog ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  )}
                </div>
                {showLog && (
                  <ul className="space-y-1 border-l border-[#222F43] pl-3" data-testid="detail-changelog">
                    {log.map((l, i) => (
                      <li key={i} className="text-[11px] text-slate-400">
                        <span className="text-slate-200">{LOG_LABEL[l.type] || l.type}</span>
                        {l.from && l.to && <> · {l.from} → {l.to}</>}
                        {l.to && !l.from && <> · {l.to}</>}
                        {l.note && <> · {l.note}</>}
                        {l.mergedTitle && <> · “{l.mergedTitle}”</>}
                        {l.source && <span className="text-slate-500"> · {l.source}</span>}
                        <span className="text-slate-600"> · {fmtAgo(l.at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {e.private && <p className="text-xs text-pink-300/90" data-testid="detail-private-note">Only you can see this event. It's always in your feed.</p>}
                {!e.private && <Button variant="outline" onClick={() => onStar(e)}
                  className={`w-full bg-transparent ${isStarred ? "border-amber-500/60 text-amber-400 hover:bg-amber-500/10" : "border-[#334566] text-slate-300 hover:border-amber-500/60 hover:text-amber-300"}`}
                  data-testid="detail-star-btn">
                  <Star className="h-4 w-4 mr-1.5" fill={isStarred ? "currentColor" : "none"} /> {isStarred ? "Starred — in your feed" : "Star for my feed"}
                </Button>}
                {(isAdmin || e.private) && (
                  <div className="flex gap-2 pt-1">
                    {isAdmin && !e.private && onTogglePublish && (
                      <Button onClick={() => onTogglePublish(e)} data-testid="detail-publish-btn"
                        className={`flex-1 font-bold ${isUnpublished(e) ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-transparent border border-amber-500/50 text-amber-300 hover:bg-amber-500/10"}`}>
                        {isUnpublished(e) ? <><Eye className="h-4 w-4 mr-1.5" /> Publish</> : <><EyeOff className="h-4 w-4 mr-1.5" /> Hide</>}
                      </Button>
                    )}
                    <Button variant="outline" className="border-[#334566] bg-transparent flex-1" onClick={() => onEdit(e)} data-testid="detail-edit-btn"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
                    <Button variant="outline" className="border-red-500/40 bg-transparent text-red-400 flex-1" onClick={() => onDelete(e)} data-testid="detail-del-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Delete</Button>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
